import { getObjectApiNamesFromFunnelMap, getObjectApiNamesFromRecordTypes } from '../helpers';
import { sortLexicographically } from '../../../lib/sortLexicographically';
import { DocumentationPills } from '../../../reducers/global/globalReducerTypes';
import { ObjectWithPills } from '../types';
import { isCpqType, CPQ_NAMESPACE_PREFIXES } from '@server/documentation';
import { ObjectType } from '../../parser/ParserTypes';

export const isCustomObject = (objectType: ObjectTypeName) =>
  isCustomObjectByName(objectType.objectType);
export const isCustomObjectByName = (objectName: string) => objectName.endsWith('__c');

export const isPlatformEvent = (objectType: ObjectTypeName) =>
  isPlatformEventByName(objectType.objectType);
export const isPlatformEventByName = (objectName: string) => objectName.endsWith('__e');

export const getObjectsWithPillsPerCategory = ({
  recordTypesData,
  funnelsData,
  objectTypeNames,
  pills,
  transientObjects,
  parsedObjects,
}: {
  recordTypesData: RecordTypesData;
  funnelsData: FunnelsData;
  objectTypeNames: ObjectTypeName[];
  pills?: DocumentationPills;
  transientObjects: { [objectName: string]: boolean };
  parsedObjects?: ObjectType[];
}) => {
  const cpqNotInFunnelMap: ObjectTypeName[] = [];
  const favorites: ObjectTypeName[] = [];
  const standardObjectsNotInFunnelMap: ObjectTypeName[] = [];
  const customObjectsNotInFunnelMap: ObjectTypeName[] = [];
  const platformEventsNotInFunnelMap: ObjectTypeName[] = [];

  const objectAPINamesInFunnel = getObjectApiNamesFromFunnelMap(funnelsData);
  const objectAPINamesInRecordTypes = getObjectApiNamesFromRecordTypes(recordTypesData);

  objectTypeNames.forEach((objectType) => {
    const parsedObject = parsedObjects?.find((object) => object.name === objectType.objectType);
    const _pills = pills?.[objectType.objectType];
    const objectTypeWithPills = { ...objectType, pills: _pills };

    const isInFunnels = objectAPINamesInFunnel.includes(objectTypeWithPills.objectType);
    const isInRecordTypes = objectAPINamesInRecordTypes.includes(objectTypeWithPills.objectType);
    const isTransientObject = transientObjects?.[objectTypeWithPills.objectType];

    if (isInFunnels || isInRecordTypes || isTransientObject || parsedObject?.favorite) {
      favorites.push(objectTypeWithPills);
      return;
    }

    if (!isTransientObject) {
      if (isCpqType(objectTypeWithPills.objectType)) {
        cpqNotInFunnelMap.push(objectTypeWithPills);
      } else {
        if (isPlatformEvent(objectTypeWithPills)) {
          platformEventsNotInFunnelMap.push(objectTypeWithPills);
        } else if (isCustomObject(objectTypeWithPills)) {
          customObjectsNotInFunnelMap.push(objectTypeWithPills);
        } else {
          standardObjectsNotInFunnelMap.push(objectTypeWithPills);
        }
      }
    }
  });

  //by product definition, if CPQ objects exists,
  //we should render CPQ_OBJECTS = ['Product2', 'Pricebook2', 'PricebookEntry'] in CPQ tab, otherwise in standard objects tab
  let hasRealCPQObjects = false;
  for (const object of cpqNotInFunnelMap) {
    if (CPQ_NAMESPACE_PREFIXES.some((prefix) => object.objectType.startsWith(prefix))) {
      hasRealCPQObjects = true;
      break;
    }
  }

  const standardObjects = hasRealCPQObjects
    ? standardObjectsNotInFunnelMap
    : [...standardObjectsNotInFunnelMap, ...cpqNotInFunnelMap];

  const cpq = hasRealCPQObjects ? cpqNotInFunnelMap : [];

  return {
    cpq: (sortLexicographically({
      items: cpq,
      sortKey: 'label',
    }) ?? []) as ObjectWithPills[],
    favorites: (sortLexicographically({
      items: favorites,
      sortKey: 'label',
    }) ?? []) as ObjectWithPills[],
    customObjectsNotInFM: (sortLexicographically({
      items: customObjectsNotInFunnelMap,
      sortKey: 'label',
    }) ?? []) as ObjectWithPills[],
    platformEventsNotInFM: (sortLexicographically({
      items: platformEventsNotInFunnelMap,
      sortKey: 'label',
    }) ?? []) as ObjectWithPills[],
    standardObjectsNotInFunnelMap: (sortLexicographically({
      items: standardObjects,
      sortKey: 'label',
    }) ?? []) as ObjectWithPills[],
  };
};
